/**
 * Options Trading API URLs
 */

const BASE_URL = '/api/options';

export const OPTIONS_URLS = {
  // Get spot price for an index
  SPOT_PRICE: (symbol: string) => `${BASE_URL}/spot/${symbol}`,

  // Get option chain data
  OPTION_CHAIN: (symbol: string, expiryDate?: string) => {
    const url = `${BASE_URL}/chain/${symbol}`;
    return expiryDate ? `${url}?expiry_date=${expiryDate}` : url;
  },

  // Analyze option chain with AI (synchronous - waits for completion)
  ANALYZE: `${BASE_URL}/analyze`,

  // Async analysis endpoints
  ANALYZE_ASYNC: `${BASE_URL}/analyze/async`,
  ANALYSIS_STATUS: (analysisId: number) => `${BASE_URL}/analysis/status/${analysisId}`,

  // Get max pain calculation
  MAX_PAIN: (symbol: string, expiryDate?: string) => {
    const url = `${BASE_URL}/max-pain/${symbol}`;
    return expiryDate ? `${url}?expiry_date=${expiryDate}` : url;
  },

  // Save analysis to database
  SAVE_ANALYSIS: `${BASE_URL}/analysis/save`,

  // Get analysis history
  ANALYSIS_HISTORY: (symbol: string, expiryDate?: string, limit: number = 10) => {
    const url = `${BASE_URL}/analysis/history/${symbol}`;
    const params = new URLSearchParams();
    if (expiryDate) params.append('expiry_date', expiryDate);
    params.append('limit', limit.toString());
    return `${url}?${params.toString()}`;
  },

  // Get latest analysis
  LATEST_ANALYSIS: (symbol: string, expiryDate?: string) => {
    const url = `${BASE_URL}/analysis/latest/${symbol}`;
    return expiryDate ? `${url}?expiry_date=${expiryDate}` : url;
  },

  // Get technical indicators for an index
  TECHNICAL_INDICATORS: (symbol: string, refresh: boolean = false) => {
    const url = `${BASE_URL}/technical-indicators/${symbol}`;
    return refresh ? `${url}?refresh=true` : url;
  },

  // WebSocket credentials
  WEBSOCKET_CREDENTIALS: `${BASE_URL}/websocket/credentials`,

  // Get instrument tokens for WebSocket subscription
  INSTRUMENT_TOKENS: (symbol: string) => `${BASE_URL}/instruments/${symbol}`,

  // Market daily data (FII/DII, VIX)
  MARKET_DAILY_DATA: (targetDate?: string, refresh: boolean = false) => {
    const params = new URLSearchParams();
    if (targetDate) params.append('target_date', targetDate);
    if (refresh) params.append('refresh', 'true');
    const queryString = params.toString();
    return queryString ? `${BASE_URL}/market-daily-data?${queryString}` : `${BASE_URL}/market-daily-data`;
  },

  // Market daily data history
  MARKET_DAILY_DATA_HISTORY: (days: number = 7) => `${BASE_URL}/market-daily-data/history?days=${days}`,

  // Save market daily data manually
  SAVE_MARKET_DAILY_DATA: `${BASE_URL}/market-daily-data`,

  // Live price update
  LIVE_PRICE_UPDATE: `${BASE_URL}/live-price/update`,

  // News endpoints
  NEWS_SYNC: (symbol: string, includeGlobal: boolean = true, includeIndian: boolean = true) => {
    const params = new URLSearchParams();
    if (!includeGlobal) params.append('include_global', 'false');
    if (!includeIndian) params.append('include_indian', 'false');
    const queryString = params.toString();
    return queryString ? `${BASE_URL}/news/${symbol}?${queryString}` : `${BASE_URL}/news/${symbol}`;
  },
  NEWS_ASYNC: (symbol: string = 'NIFTY', includeGlobal: boolean = true, includeIndian: boolean = true) => {
    const params = new URLSearchParams();
    params.append('symbol', symbol);
    if (!includeGlobal) params.append('include_global', 'false');
    if (!includeIndian) params.append('include_indian', 'false');
    return `${BASE_URL}/news/async?${params.toString()}`;
  },
  NEWS_STATUS: (analysisId: number) => `${BASE_URL}/news/status/${analysisId}`,
  NEWS_LATEST: (symbol: string) => `${BASE_URL}/news/latest/${symbol}`,
  NEWS_HISTORY: (symbol: string, limit: number = 10) => `${BASE_URL}/news/history/${symbol}?limit=${limit}`,

  // X Feed endpoints
  X_FEED_ASYNC: `${BASE_URL}/x-feed/async`,

  // Chat endpoint
  CHAT: `${BASE_URL}/chat`,

  // OI Snapshot endpoints
  OI_SNAPSHOT_SAVE: `${BASE_URL}/oi-snapshot`,
  OI_SNAPSHOTS: (symbol: string, expiryDate?: string, hours: number = 8) => {
    const params = new URLSearchParams();
    if (expiryDate) params.append('expiry_date', expiryDate);
    params.append('hours', hours.toString());
    return `${BASE_URL}/oi-snapshots/${symbol}?${params.toString()}`;
  },
  OI_CHANGE: (symbol: string, expiryDate?: string) => {
    const url = `${BASE_URL}/oi-change/${symbol}`;
    return expiryDate ? `${url}?expiry_date=${expiryDate}` : url;
  },
  PCR_TREND: (symbol: string, hours: number = 8) => `${BASE_URL}/pcr-trend/${symbol}?hours=${hours}`,
};
